import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { api } from '../api';

const PLACEHOLDER_IMG = 'https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=1200&h=600&fit=crop';

const CATEGORY_COLORS = {
  'umrah-tips':    { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  'packing-guide': { bg: 'bg-amber-100',   text: 'text-amber-800'   },
  'visa-updates':  { bg: 'bg-blue-100',    text: 'text-blue-800'    },
  'dua-guide':     { bg: 'bg-purple-100',  text: 'text-purple-800'  },
  'news':          { bg: 'bg-red-100',     text: 'text-red-800'     },
  'travel-guide':  { bg: 'bg-teal-100',    text: 'text-teal-800'    },
  'hajj-tips':     { bg: 'bg-orange-100',  text: 'text-orange-800'  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' });
};

const BlogDetailPage = ({ navigate, slug }) => {
  const [post, setPost]         = useState(null);
  const [related, setRelated]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (slug) loadPost(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadPost = async (postSlug) => {
    setLoading(true);
    setNotFound(false);
    try {
      const data = await api.getBlogPostBySlug(postSlug);
      setPost(data);
      // Load related posts from same category
      try {
        const rel = await api.getBlogPosts({ category: data.category });
        setRelated(rel.filter((p) => p.id !== data.id).slice(0, 3));
      } catch (_) {}
    } catch (_) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar navigate={navigate} />
        <div className="max-w-3xl mx-auto px-4 pt-32 pb-16">
          <div className="animate-pulse space-y-6">
            <div className="h-4 bg-sand rounded w-32" />
            <div className="h-10 bg-sand rounded w-full" />
            <div className="h-10 bg-sand rounded w-3/4" />
            <div className="h-72 bg-sand rounded-2xl" />
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-sand rounded" style={{ width: `${80 + Math.random() * 20}%` }} />
            ))}
          </div>
        </div>
        <Footer navigate={navigate} />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar navigate={navigate} />
        <div className="max-w-xl mx-auto px-4 pt-40 pb-16 text-center">
          <p className="text-6xl mb-4">🔍</p>
          <h2 className="text-2xl font-semibold text-ink mb-3">Article not found</h2>
          <p className="text-ink/50 mb-8">This article may have been moved or removed.</p>
          <button
            onClick={() => navigate('blog')}
            className="gradient-gold text-ink px-6 py-3 rounded-lg font-semibold">
            Back to Blog
          </button>
        </div>
        <Footer navigate={navigate} />
      </div>
    );
  }

  const catColor = CATEGORY_COLORS[post.category] || { bg: 'bg-sand', text: 'text-ink' };
  const tags = post.tags ? post.tags.split(',').map((t) => t.trim()).filter(Boolean) : [];

  return (
    <div className="min-h-screen bg-cream">
      <Navbar navigate={navigate} />

      {/* Hero Image */}
      <div className="relative h-[40vh] sm:h-[50vh] lg:h-[55vh] overflow-hidden bg-ink">
        <img
          src={post.featured_image || PLACEHOLDER_IMG}
          alt={post.title}
          className="w-full h-full object-cover opacity-80"
          onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-4 pb-8 sm:pb-12">
          <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase ${catColor.bg} ${catColor.text} mb-4`}>
            {post.category_display}
          </span>
          <h1 className="text-white font-display text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
            {post.title}
          </h1>
        </div>
      </div>

      {/* Article */}
      <div className="max-w-4xl mx-auto px-4">

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-4 py-5 border-b border-sand mb-8">
          <button
            onClick={() => navigate('blog')}
            className="flex items-center gap-1.5 text-primary text-sm font-medium hover:underline">
            ← Blog
          </button>
          <div className="w-px h-4 bg-sand" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
              {post.author_name.charAt(0)}
            </div>
            <span className="text-sm text-ink font-medium">{post.author_name}</span>
          </div>
          <div className="w-px h-4 bg-sand hidden sm:block" />
          <span className="text-sm text-ink/50">{formatDate(post.published_at)}</span>
          <div className="w-px h-4 bg-sand hidden sm:block" />
          <span className="text-sm text-ink/50">{post.read_time_minutes} min read</span>
          <div className="w-px h-4 bg-sand hidden sm:block" />
          <span className="text-sm text-ink/40">{post.views_count} views</span>
        </div>

        {/* Excerpt */}
        {post.excerpt && (
          <p className="text-lg text-ink/70 leading-relaxed mb-8 font-medium border-l-4 border-accent pl-5 italic">
            {post.excerpt}
          </p>
        )}

        {/* Content */}
        <div
          className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-ink prose-p:text-ink/75 prose-p:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-blockquote:border-accent prose-blockquote:text-ink/70 mb-12"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10 pt-6 border-t border-sand">
            <span className="text-sm text-ink/40 font-medium mr-1">Tags:</span>
            {tags.map((tag) => (
              <span key={tag} className="px-3 py-1 bg-cream border border-sand rounded-full text-xs text-ink/60 font-medium">
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Share row */}
        <div className="flex flex-wrap items-center gap-3 mb-12 p-5 bg-white rounded-2xl shadow-sm">
          <p className="text-sm font-semibold text-ink mr-2">Share:</p>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(post.title + ' - ' + window.location.href)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-[#25D366] text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.5 3.5A11 11 0 006.3 18.7L3 21l2.4-3.2A11 11 0 1020.5 3.5z" />
            </svg>
            WhatsApp
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-[#1877F2] text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90">
            Facebook
          </a>
          <button
            onClick={() => { navigator.clipboard.writeText(window.location.href); }}
            className="flex items-center gap-1.5 bg-sand text-ink px-4 py-2 rounded-lg text-sm font-medium hover:bg-cream border border-sand/80">
            Copy link
          </button>
        </div>

        {/* CTA */}
        <Reveal className="mb-16 bg-ink text-white rounded-2xl p-8 sm:p-10 text-center">
          <p className="text-accent text-[11px] tracking-[0.25em] uppercase mb-3">Plan Your Journey</p>
          <h3 className="text-2xl sm:text-3xl font-display mb-3">Ready to perform Umrah?</h3>
          <p className="text-sand/70 mb-6 max-w-lg mx-auto">
            Explore our curated packages and let our team guide you every step of the way.
          </p>
          <button
            onClick={() => navigate('category', { category: 'umrah-packages' })}
            className="gradient-gold text-ink px-8 py-3 rounded-lg font-semibold">
            Browse packages
          </button>
        </Reveal>
      </div>

      {/* Related Posts */}
      {related.length > 0 && (
        <div className="bg-white py-14 sm:py-16">
          <div className="max-w-7xl mx-auto px-4">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-display text-ink mb-8">Related Articles</h2>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Reveal key={p.id} delay={i * 70}>
                  <div
                    onClick={() => navigate('blog-detail', { slug: p.slug })}
                    className="group cursor-pointer bg-cream rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                    <div className="aspect-[16/9] overflow-hidden bg-sand">
                      <img
                        src={p.featured_image || PLACEHOLDER_IMG}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-ink/40 mb-1">{formatDate(p.published_at)} · {p.read_time_minutes} min</p>
                      <h4 className="font-semibold text-ink leading-snug group-hover:text-primary transition-colors line-clamp-2">
                        {p.title}
                      </h4>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer navigate={navigate} />
    </div>
  );
};

export default BlogDetailPage;
